import { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../api/axios';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // First decode the token to validate it and get fallback user data
          const decoded = jwtDecode(token);

          try {
            // Try to fetch full profile and await the import
            const { userService } = await import('../api/users');
            const profile = await userService.getProfile();
            setUser(profile);
          } catch (profileError) {
            console.warn("Could not fetch full profile, using token data fallback.", profileError);
            setUser({
              user_id: decoded.user_id,
              username: decoded.username || 'User',
              role: decoded.role || 'tutor',
            });
          }
        } catch (error) {
          console.error("Invalid token on init:", error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/token/', { username, password });
      const { access, refresh } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Fetch full profile immediately
      const { userService } = await import('../api/users');
      const profile = await userService.getProfile();
      setUser(profile);

      return true;
    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    // Optional: Call backend to blacklist refresh token if implemented
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
